import React from "react";

const Analytics = ({ activeTab, dashboardata }) => {
  if (activeTab !== 1 || !dashboardata) return null;

  return (
    <div className="animate-fadeIn pb-8">
      {/* Header Section */}
      <div className="text-left pb-6">
        <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
        <p className="text-sm text-gray-500 mt-1">
          Real-time overview of referrals, enrollments, and campaign performance.
        </p>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Card 1: Total Referrals */}
        <div className="bg-gradient-to-br from-[#004f6b] to-[#003e54] rounded-xl shadow-lg p-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <svg className="w-24 h-24" fill="currentColor" viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg>
          </div>
          <div className="text-lg font-medium opacity-90">Total Referrals</div>
          <div className="text-4xl font-bold mt-4">{dashboardata.totalReferral || 0}</div>
          <div className="mt-2 text-xs text-blue-200">All time referrals</div>
        </div>

        {/* Card 2: Total Enrolled */}
        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl shadow-lg p-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <svg className="w-24 h-24" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /></svg>
          </div>
          <div className="text-lg font-medium opacity-90">Total Enrolled</div>
          <div className="text-4xl font-bold mt-4">{dashboardata.totalEnrolledCount || 0}</div>
          <div className="mt-2 text-xs text-green-100">Successfully enrolled</div>
        </div>

        {/* Card 3: In Progress */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative overflow-hidden  transition-shadow">
          <div className="text-lg font-semibold text-gray-800 mb-4">In Progress</div>
          <div className="space-y-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500">Fee Paid</span>
              <span className="font-bold text-gray-900">0</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500">Doc. Verified</span>
              <span className="font-bold text-gray-900">0</span>
            </div>
            <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-100">
              <span className="text-gray-700 font-medium">Other Pending</span>
              <span className="font-bold text-[#004f6b] text-lg">{dashboardata.totalPendingCount || 0}</span>
            </div>
          </div>
        </div>

        {/* Card 4: Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between  transition-shadow">
          <div>
            <div className="text-lg font-semibold text-gray-800 mb-2">Recent Activity</div>
            <div className="space-y-4 mt-4">
              <div className="flex justify-between items-end">
                <span className="text-sm text-gray-500">Last 7 Days</span>
                <span className="text-2xl font-bold text-[#004f6b]">{dashboardata.countLast7DaysReferral || 0}</span>
              </div>
              <div className="flex justify-between items-end">
                <span className="text-sm text-gray-500">Last 30 Days</span>
                <span className="text-2xl font-bold text-[#004f6b]">{dashboardata.countLast30DaysReferral || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">

        {/* Referrals by Program */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 bg-gray-50/50 border-b border-gray-100">
            <h3 className="text-lg font-bold text-gray-800">Referrals by Program</h3>
            <p className="text-xs text-gray-500 mt-1">Breakdown of interested programs</p>
          </div>
          <div className="p-0">
            {dashboardata?.referralByProgram?.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="py-3 px-6 text-xs font-semibold text-gray-600  tracking-wider">Program Name</th>
                      <th className="py-3 px-6 text-xs font-semibold text-gray-600 tracking-wider text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {dashboardata.referralByProgram.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-6 text-sm text-gray-700 font-medium">{item.program}</td>
                        <td className="py-3 px-6 text-sm text-gray-900 font-bold text-right">{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-400 text-sm">No program data available</div>
            )}
          </div>
        </div>

        {/* No Data / Placeholder Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex items-center justify-center p-8 text-center">
          <div>
            <svg className="w-16 h-16 text-gray-200 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <div className="text-xl font-semibold text-gray-400">Additional Insights</div>
            <div className="text-sm text-gray-400 mt-2 max-w-xs mx-auto">
              Select a wider date range to view detailed historical trends and deeper analytics.
            </div>
          </div>
        </div>
      </div>

      {/* Top Lists Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* Top Mediums */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gray-50/50">
            <h3 className="text-lg font-bold text-gray-800">Top Mediums</h3>
            <p className="text-xs text-gray-500">Performance in the last 30 days</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-xs font-bold text-gray-600">#</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-600">Medium</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-600 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dashboardata?.top10MediumLast30Days?.length > 0 ? (
                  dashboardata.top10MediumLast30Days.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3 text-sm text-gray-400">{index + 1}</td>
                      <td className="px-6 py-3 text-sm font-medium text-gray-800">
                        <span className="capitalize px-2 py-1 text-black rounded text-xs">
                          {item.medium || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm font-bold text-gray-900 text-right">{item.count}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="3" className="px-6 py-8 text-center text-sm text-gray-400">No data available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Campaigns */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gray-50/50">
            <h3 className="text-lg font-bold text-gray-800">Top Campaigns</h3>
            <p className="text-xs text-gray-500">Performance in the last 30 days</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-xs font-bold text-gray-600">#</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-600">Campaign</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-600 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dashboardata?.top10CampaignLast30Days?.length > 0 ? (
                  dashboardata.top10CampaignLast30Days.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3 text-sm text-gray-400">{index + 1}</td>
                      <td className="px-6 py-3 text-sm font-medium text-gray-800">
                        <span className="px-2 py-1 text-black rounded text-xs truncate max-w-[150px] inline-block" title={item.campaign}>
                          {item.campaign || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm font-bold text-gray-900 text-right">{item.count}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="3" className="px-6 py-8 text-center text-sm text-gray-400">No data available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* <div className="text-center py-6 mt-8 border-t border-gray-200">
        <p className="text-xs text-gray-400">Powered by VIGNAN Online </p>
      </div> */}

    </div>
  );
};

export default Analytics;
